import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

/**
 * Retries failed API requests with exponential backoff.
 *
 * Only retries on connection errors (status 0 / ERR_EMPTY_RESPONSE) — not
 * on 4xx/5xx which are intentional responses from the server.
 *
 * Backoff delays: 2s → 4s → 8s → gives up (3 attempts total).
 * After all retries are exhausted the original error is rethrown so existing
 * catchError / ResourceFailed flows still work normally.
 */
const RETRY_DELAYS_MS = [2000, 4000, 8000];

@Injectable()
export class ApiBackoffInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only apply to API calls, not graph / version.json / other assets
    if (!req.url.includes('/api/')) {
      return next.handle(req);
    }

    let attempt = 0;

    return next.handle(req).pipe(
      retryWhen((errors$) =>
        errors$.pipe(
          mergeMap((error: HttpErrorResponse) => {
            const isConnectionError = error.status === 0;
            const hasRetriesLeft = attempt < RETRY_DELAYS_MS.length;

            if (!isConnectionError || !hasRetriesLeft) {
              return throwError(() => error);
            }

            const delay = RETRY_DELAYS_MS[attempt];
            attempt++;
            console.warn(
              `[ApiBackoff] Connection error on ${req.method} ${req.urlWithParams} — ` +
                `retry ${attempt}/${RETRY_DELAYS_MS.length} in ${delay / 1000}s`,
            );
            return timer(delay);
          }),
        ),
      ),
    );
  }
}
